import React from "react";
import {FormProvider, useFieldArray, useForm} from "react-hook-form";
import {Form} from "./QuestionForm.styles";
import FormActionButtons from "../common/FormActionButtons/FormActionButtons";
import {Question} from "./components/patterns/Question";
import {Grid} from "@mui/material";
import SameQuestionButton from "./components/atoms/SameQuestionButton/SameQuestionButton";
import AddSubquestionButton from "./components/atoms/AddSubquestionButton/AddSubquestionButton";
import {DividerWrapper} from "./components/patterns/Question/Question.styles";
import {QuestionTypes} from "../../static/enums/Questions";
import {Nullable} from "../../@typings/typings";
import {snackbarStore} from "../../mobx/ui/SnackbarStore";
import {DeleteQuestionButton} from "./components/atoms/DeleteQuestionButton";

export interface QuestionSkillForm extends Record<string, any> {
	id: string;
	title: string;
	order: number;
	created_at: string;
	updated_at: string;
}

export interface SubquestionForm extends Record<string, any> {
	id: string;
	difficulty: number;
	type: QuestionTypes;
	order: number;
	category: "for_assignments" | "for_test_exams" | undefined;
	hint: string;
	solution: string;
	correct_answer: Nullable<string | string[]>;
	answer_config: Nullable<string | string[]>;
	parent_question?: { id: string };
}

export interface IQuestionForm {
	id: string;
	difficulty: number;
	order: number;
	class: QuestionTypes;
	category: "for_assignments" | "for_test_exams" | undefined;
	hint: string;
	solution: string;
	correct_answer: Nullable<string | string[]>;
	answer_config: Nullable<string | string[]>;
	children_questions: SubquestionForm[];
}

const getDefaultQuestion = (): Partial<IQuestionForm> => ({
	difficulty: 0,
	order: 0,
	category: "for_assignments",
	class: QuestionTypes.input_text,
	hint: "",
	solution: "",
});

export default function QuestionForm({
	defaultQuestion = getDefaultQuestion(),
	onSubmit = () => {},
	loading = false,
}: {
	loading?: boolean;
	defaultQuestion?: any;
	onSubmit?: (data: any) => void;
}) {
	const methods = useForm<IQuestionForm>({
		defaultValues: defaultQuestion,
	});
	const { handleSubmit, control, register, setValue } = methods;
	const { fields, append, remove } = useFieldArray({
		control,
		name: "children_questions",
	});
	register("order", {
		value: 0,
	});
	const onInvalid = () => {
		snackbarStore.notifyInvalidForm();
	};

	const onAddSubquestion = async () => {
		append(getDefaultQuestion());
	};

	const onRemoveSubquestion = (index: number) => {
		remove(index);
	};

	return (
		<FormProvider {...methods}>
			<Form container direction={"column"} {...{ component: "form" }} onSubmit={handleSubmit(onSubmit, onInvalid)}>
				<Grid container item justifyContent={"space-between"} sx={{ padding: "0 8px" }}>
					<DeleteQuestionButton />
					<SameQuestionButton />
				</Grid>
				<Question />
				{renderSubquestions()}
				<Grid container item justifyContent={"space-between"} sx={{ padding: "8px" }}>
					<AddSubquestionButton onClick={onAddSubquestion} />
					<SameQuestionButton />
				</Grid>
				<FormActionButtons loading={loading} />
			</Form>
		</FormProvider>
	);

	function renderSubquestions() {
		return fields.map((field, i) => {
			setValue(`children_questions.${i}.order`, i + 1);
			return (
				<React.Fragment key={field.id}>
					<DividerWrapper />
					<Question
						prefix={`children_questions.${i}.`}
						isSubquestion
						onRemoveSubquestion={() => onRemoveSubquestion(i)}
					/>
				</React.Fragment>
			);
		});
	}
}
